// A window's endpoint events, as themselves.
//
// The ambient reading next door (traceEndpointCorroboration) counts kinds, and
// a count is not an event: "496 processes · 04:45 PM–04:47 PM" is a tally of
// what happened, and a reader who opens a window wants the thing that happened
// — the command line a process ran, the path a file event touched.
//
// The material is the DETAIL projection, the one already fetched for name
// matching (traceTurnClockFetch's fetchEndpointEventDetails). There is no third
// query here and there should not be one: event_data is the heavy column, and a
// second way of asking for it would double the cost of the expensive leg to
// render the same rows.
//
// That inheritance sets one hard boundary. The detail leg is narrowed to
// MATCHABLE_EVENT_TYPES — processes and file events — because a DnsQuery and a
// NetworkConnection can carry no name the turn reading also holds. So this
// reading can enumerate processes and file events and nothing else, while the
// per-kind totals beside it still count every kind. The caller is expected to
// say so; a list that silently omits a kind the totals include is the reading
// claiming to be complete when it is not.
//
// Three rules, and each of them is a refusal to overstate:
//
//   FOLD. The same command line three hundred times is ONE row with a count.
//   A window on a working machine is mostly one shell forking; three hundred
//   identical rows bury the four that matter and say nothing the count does not.
//
//   RANK, but only on what can be explained. Name matches first — those are the
//   rows the turn reading already named. Then the LEAST repeated: something that
//   ran once inside this window says more about the window than something that
//   ran three hundred times, which is by construction background. There is no
//   relevance score here because there is no relevance signal here, and an
//   unexplainable ordering would be worse than an arbitrary one.
//
//   CAP, and say what the cap dropped. A window with 496 processes must not
//   render 496 rows, and must not imply it showed everything either. Because the
//   ordering is least-repeated-first, what a cap hides is exactly the most
//   repeated — which is the one thing the per-kind totals below already carry.

import { normalizeSpace } from "./traceEndpointMatch.js";

// A cap on ROWS, not on events: the rows are already folded, so twelve of them
// can account for hundreds of events. Twelve is what fits in the expansion
// without the panel becoming a scroll surface inside a scroll surface.
export const EVENT_ROW_CAP = 12;

/**
 * How one event reads as a line, or null when it names nothing.
 *
 * The verbs agree with the matcher's (traceEndpointMatch's fileTargets) but a
 * RENAME is deliberately read differently: the matcher needs both paths as
 * separate candidates, because either side may be the one a turn named, while
 * a reader wants the one thing that happened.
 *
 * Which is why TWO readings come back. `text` is for the page. `observed` is
 * the raw name — or names — the matcher would report for the same event, and is
 * the only thing a match can be compared against; a rename's display line is a
 * folded pair and equals neither side of it.
 */
export function eventLine(event) {
  const kind = event?.kind;
  const data = event?.data;
  if (!kind || !data) return null;
  if (kind === "ProcessStart") {
    const text = normalizeSpace(data.command_line || data.image_name || "");
    return text ? { text, verb: "ran", observed: [text] } : null;
  }
  if (kind === "FileState" || kind === "FileDelete") {
    const path = data.filepath;
    if (!path) return null;
    return { text: String(path), verb: kind === "FileDelete" ? "deleted" : "written", observed: [String(path)] };
  }
  if (kind === "FileRename") {
    const both = [data.original_filepath, data.new_filepath].filter(Boolean).map(String);
    if (!both.length) return null;
    return { text: both.length === 2 ? `${both[0]} → ${both[1]}` : both[0], verb: "renamed", observed: both };
  }
  return null;
}

/**
 * A window's events folded to one row per distinct thing that happened.
 *
 * @returns [{ key, kind, verb, text, observed, count, firstTs, lastTs }] —
 *          unordered
 */
export function foldWindowEvents(events) {
  const byKey = new Map();
  for (const event of events || []) {
    const read = eventLine(event);
    if (!read) continue;
    const key = `${event.kind}|${read.verb}|${read.text}`;
    let row = byKey.get(key);
    if (!row) {
      row = {
        key,
        kind: event.kind,
        verb: read.verb,
        text: read.text,
        observed: read.observed,
        count: 0,
        firstTs: null,
        lastTs: null,
      };
      byKey.set(key, row);
    }
    row.count += 1;
    if (Number.isFinite(event.ts)) {
      if (row.firstTs == null || event.ts < row.firstTs) row.firstTs = event.ts;
      if (row.lastTs == null || event.ts > row.lastTs) row.lastTs = event.ts;
    }
  }
  return [...byKey.values()];
}

/**
 * The rows an expanded window shows, in the order it shows them.
 *
 * @param events   the detail projection for the window
 * @param matches  what matchWindowEvents found, so a corresponding row leads
 * @param cap      how many rows earn a line
 * @returns { rows, distinct, read, hidden, unreadable }
 *
 * `read` is how many raw events the shown-and-hidden rows account for — the
 * honest denominator for the fold, and NOT the window's event count, which
 * includes kinds this projection never asked for. `unreadable` counts events
 * that carried no name at all; they are still real, so they are reported rather
 * than quietly dropped from the arithmetic.
 */
export function windowEventRows(events, matches, { cap = EVENT_ROW_CAP } = {}) {
  const named = new Set((matches || []).map((m) => `${m.kind}|${m.observed}`));
  // Compared on the NAME the event carried, never on the line it renders as.
  // A rename's line folds two paths into one arrow and equals neither of them,
  // so a rename the matcher did find would show here as an ordinary unmatched
  // row — visible nowhere, because "Name matches" above still listed it.
  // EITHER path counts: the turn may have named the source or the destination,
  // and the matcher will have matched whichever one it was.
  const rows = foldWindowEvents(events).map((r) => ({
    ...r,
    matched: r.observed.some((o) => named.has(`${r.kind}|${o}`)),
  }));
  rows.sort(
    (a, b) =>
      (b.matched ? 1 : 0) - (a.matched ? 1 : 0) ||
      a.count - b.count ||
      (a.firstTs ?? Infinity) - (b.firstTs ?? Infinity) ||
      a.key.localeCompare(b.key),
  );
  const shown = cap > 0 ? rows.slice(0, cap) : rows;
  const read = rows.reduce((n, r) => n + r.count, 0);
  const total = (events || []).filter((e) => e && e.data).length;
  return {
    rows: shown,
    distinct: rows.length,
    read,
    hidden: rows.length - shown.length,
    unreadable: Math.max(0, total - read),
  };
}
